import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { publicApi, Move, Pattern, isPlanRequired } from '../services/api';
import PlanRequired from './PlanRequired';
import { useAuth } from '../context/AuthContext';
import { useModalA11y } from '../hooks/useModalA11y';
import './DemonstrationUploadModal.css';

interface DemonstrationUploadModalProps {
  move?: Move;
  pattern?: Pattern;
  onClose: () => void;
  onSuccess?: () => void;
  dancerId?: string;
}

const DemonstrationUploadModal: React.FC<DemonstrationUploadModalProps> = ({
  move,
  pattern,
  onClose,
  onSuccess,
  dancerId: dancerIdProp,
}) => {
  if ((move && pattern) || (!move && !pattern)) {
    throw new Error('DemonstrationUploadModal requires exactly one of `move` or `pattern`');
  }
  const parentTitle = move ? move.title : pattern!.title;
  const parentKind: 'move' | 'pattern' = move ? 'move' : 'pattern';
  const { isAuthenticated } = useAuth();
  const [selectedDancerId, setSelectedDancerId] = useState(dancerIdProp || '');
  const [caption, setCaption] = useState('');
  const [recordedAt, setRecordedAt] = useState('');
  const [error, setError] = useState('');
  const [planRequired, setPlanRequired] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useModalA11y(onClose);

  // Auto-detect dancer from auth when authenticated
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => publicApi.auth.me(),
    enabled: isAuthenticated && !dancerIdProp,
  });

  useEffect(() => {
    if (meData?.dancer?.id && !dancerIdProp) {
      setSelectedDancerId(String(meData.dancer.id));
    }
  }, [meData, dancerIdProp]);

  // Fallback: get verified dancers for dropdown when not authenticated
  const { data: dancers } = useQuery({
    queryKey: ['verified-dancers'],
    queryFn: () => publicApi.dancers.getAll(false),
    enabled: !isAuthenticated && !dancerIdProp,
  });

  const uploadMutation = useMutation({
    mutationFn: publicApi.demonstrations.upload,
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (isPlanRequired(error)) setPlanRequired(true);
      else setError(error.response?.data?.error || 'Failed to upload demonstration');
    },
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (!file.type.startsWith('video/')) {
      setError('Please select a video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      setError('Video file must be less than 100MB');
      return;
    }

    setError('');
    setSelectedFile(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedDancerId) {
      setError('Please select your profile');
      return;
    }

    if (!selectedFile) {
      setError('Please select a video file');
      return;
    }

    const formData = new FormData();
    formData.append('video', selectedFile);
    formData.append('dancerId', selectedDancerId);
    if (move) {
      formData.append('moveId', move.id);
    } else if (pattern) {
      formData.append('patternId', pattern.id);
    }
    formData.append('caption', caption);
    if (recordedAt) {
      formData.append('recordedAt', recordedAt);
    }

    uploadMutation.mutate(formData);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const showDancerDropdown = !isAuthenticated && !dancerIdProp;

  return (
    <div className="demo-upload-overlay" onClick={onClose}>
      <div
        ref={containerRef}
        className="demo-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Demonstrate ${parentTitle}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="demo-upload-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>

        <div className="demo-upload-header">
          <h2>{parentKind === 'pattern' ? 'Demonstrate This Pattern' : 'Demonstrate This Move'}</h2>
          <p className="demo-upload-move-name">{parentTitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="demo-upload-form">
          {/* Dancer Selection - only shown when not authenticated */}
          {showDancerDropdown && (
            <div className="demo-upload-field">
              <label htmlFor="dancerSelect">Your Profile *</label>
              <select
                id="dancerSelect"
                value={selectedDancerId}
                onChange={(e) => setSelectedDancerId(e.target.value)}
                className={!selectedDancerId && error ? 'error' : ''}
              >
                <option value="">Select your profile...</option>
                {dancers?.map(dancer => (
                  <option key={dancer.id} value={dancer.id}>
                    {dancer.name}
                  </option>
                ))}
              </select>
              <small className="demo-upload-hint">
                Don't see your name? Ask your instructor to add you.
              </small>
            </div>
          )}

          {/* Show dancer name when auto-detected */}
          {isAuthenticated && meData?.dancer && (
            <div className="demo-upload-field">
              <span className="demo-upload-field-label">Submitting as</span>
              <p className="demo-upload-dancer-name">{meData.dancer.name}</p>
            </div>
          )}

          {isAuthenticated && meLoading && (
            <div className="demo-upload-field">
              <span className="demo-upload-field-label">Submitting as</span>
              <p className="demo-upload-dancer-loading">Loading profile...</p>
            </div>
          )}

          {/* Video Upload */}
          <div className="demo-upload-field">
            <span className="demo-upload-field-label">Video *</span>
            <div
              className={`demo-upload-dropzone ${dragActive ? 'active' : ''} ${selectedFile ? 'has-file' : ''}`}
              role="button"
              tabIndex={0}
              aria-label="Choose a video file"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="video/*"
                hidden
              />

              {selectedFile ? (
                <div className="demo-upload-selected">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="#28a745">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span className="demo-upload-filename">{selectedFile.name}</span>
                  <span className="demo-upload-filesize">{formatFileSize(selectedFile.size)}</span>
                  <button
                    type="button"
                    className="demo-upload-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="demo-upload-placeholder">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="#666">
                    <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4zM14 13h-3v3H9v-3H6v-2h3V8h2v3h3v2z"/>
                  </svg>
                  <p>Drag and drop your video here</p>
                  <span>or click to browse</span>
                  <small>MP4, MOV, AVI - Max 100MB</small>
                </div>
              )}
            </div>
          </div>

          {/* Caption */}
          <div className="demo-upload-field">
            <label htmlFor="caption">Caption (optional)</label>
            <textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a note about your demonstration..."
              rows={2}
            />
          </div>

          {/* Date recorded */}
          <div className="demo-upload-field">
            <label htmlFor="recordedAt">Date recorded (optional)</label>
            <input
              id="recordedAt"
              type="date"
              value={recordedAt}
              onChange={(e) => setRecordedAt(e.target.value)}
            />
            <small className="demo-upload-hint">
              If this video was recorded on a different date, set it here.
            </small>
          </div>

          {/* Error */}
          {error && <div className="demo-upload-error">{error}</div>}
          {planRequired && <PlanRequired perk="feedback" compact />}

          {/* Actions */}
          <div className="demo-upload-actions">
            <button type="button" className="demo-upload-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="demo-upload-submit"
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Uploading...' : 'Submit Demonstration'}
            </button>
          </div>

          <p className="demo-upload-note">
            Your demonstration will be reviewed before being published.
          </p>
        </form>
      </div>
    </div>
  );
};

export default DemonstrationUploadModal;
